import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import VoiceInput from './VoiceInput';
import { FiMic } from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import './Quoter.css';
import { toZonedTime } from 'date-fns-tz';

// New components
import SearchBar from './SearchBar/SearchBar';
import MobileOverlay from './MobileFlow/MobileOverlay';
import InsuranceTypeField from './fields/InsuranceTypeField';
import OriginField from './fields/OriginField';
import DestinationField from './fields/DestinationField';
import ProductField from './fields/ProductField';
import DateField from './fields/DateField';
import PassengersField from './fields/PassengersField';
import { useResponsiveLayout } from './hooks/useResponsiveLayout';

// Timezone mapping
const COUNTRY_TIMEZONES = {
  'Argentina': 'America/Argentina/Buenos_Aires',
  'Bolivia': 'America/La_Paz',
  'Brasil': 'America/Sao_Paulo',
  'Canadá': 'America/Toronto',
  'Chile': 'America/Santiago',
  'Colombia': 'America/Bogota',
  'Costa Rica': 'America/Costa_Rica',
  'Ecuador': 'America/Guayaquil',
  'El Salvador': 'America/El_Salvador',
  'España': 'Europe/Madrid',
  'Guatemala': 'America/Guatemala',
  'Honduras': 'America/Tegucigalpa',
  'México': 'America/Mexico_City',
  'Panamá': 'America/Panama',
  'Paraguay': 'America/Asuncion',
  'Perú': 'America/Lima',
  'República Dominicana': 'America/Santo_Domingo',
  'Uruguay': 'America/Montevideo',
  'Venezuela': 'America/Caracas',
  'Mundial': 'UTC'
};

function getQuoterIdSafe() {
  if (typeof window === 'undefined') return null;
  const fromGlobal = window?.QuoterConfig?.quoterId;
  if (fromGlobal) return String(fromGlobal);
  try {
    const scripts = Array.from(document.getElementsByTagName('script'));
    for (const s of scripts) {
      const src = s.getAttribute('src');
      if (!src) continue;
      if (src.includes('cotizador.js')) {
        const u = new URL(src, window.location.origin);
        const qp = u.searchParams.get('quoterId');
        if (qp) return qp;
      }
    }
  } catch (e) {}
  try {
    const el = document.getElementById('cotizador-viajes');
    const d = el?.dataset?.quoterId;
    if (d) return d;
  } catch (e) {}
  try {
    const u = new URL(window.location.href);
    const qp = u.searchParams.get('quoterId');
    if (qp) return qp;
  } catch (e) {}
  return null;
}

function getClientKeySafe() {
  if (typeof window === 'undefined') return null;
  try {
    const scripts = Array.from(document.getElementsByTagName('script'));
    for (const s of scripts) {
      const src = s.getAttribute('src');
      if (!src) continue;
      if (src.includes('cotizador.js')) {
        const u = new URL(src, window.location.origin);
        const key = u.searchParams.get('key');
        if (key) return key;
      }
    }
  } catch (e) {}
  return null;
}

const Quoter = ({ className }) => {
  // ============ RESPONSIVE ============
  const layoutMode = useResponsiveLayout();

  // ============ STATE ============
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [selectedOrigin, setSelectedOrigin] = useState(null);
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [product, setProduct] = useState('');
  const [passengerCounts, setPassengerCounts] = useState({});
  const [minDateForPicker, setMinDateForPicker] = useState(new Date());
  const [minDaysAdjusted, setMinDaysAdjusted] = useState(false);
  const [voiceMessage, setVoiceMessage] = useState('');
  const [voiceMessageType, setVoiceMessageType] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [quoterId, setQuoterId] = useState(null);
  const [clientKey, setClientKey] = useState(null);
  const [quoterDataFromDetails, setQuoterDataFromDetails] = useState(null);
  const [geolocating, setGeolocating] = useState(false);
  const [validationErrors, setValidationErrors] = useState({
    destino: false, fechas: false, viajeros: false, producto: false, email: false
  });
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Airbnb UI state
  const [activePanel, setActivePanel] = useState(null);
  const [isMobileFlowOpen, setIsMobileFlowOpen] = useState(false);
  const [mobileActiveStep, setMobileActiveStep] = useState(null);
  const [mobileMonthsShown, setMobileMonthsShown] = useState(6);

  // Guidance modal state
  const [showGuidanceModal, setShowGuidanceModal] = useState(false);
  const [guidanceContent, setGuidanceContent] = useState({});
  const [hasShownInitialGuidance, setHasShownInitialGuidance] = useState(false);

  // ============ REFS ============
  const destinationDebounceTimer = useRef(null);
  const passengersDebounceTimer = useRef(null);
  const isHydrating = useRef(false);
  const userClearedOrigin = useRef(false);
  const selectedInsuranceRef = useRef(null);

  // ============ COMPUTED ============
  const guests = Object.values(passengerCounts).reduce((sum, count) => sum + count, 0);

  const effectiveQuoterId = useMemo(() => {
    const fromDetails = quoterDataFromDetails?.id;
    const fromState = quoterId;
    const idStr = (fromDetails != null ? String(fromDetails) : (fromState != null ? String(fromState) : '0'));
    return idStr || '0';
  }, [quoterDataFromDetails?.id, quoterId]);

  // ============ HELPERS ============
  const removeAccents = (str) => str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const customFilterOption = (option, inputValue) => {
    if (!inputValue) return false; // Solo mostrar resultados cuando hay búsqueda
    const searchValue = removeAccents(inputValue.toLowerCase().trim());
    let countryName = '';
    if (option.data) {
      if (option.data.data && option.data.data.name) {
        countryName = option.data.data.name;
      } else if (option.data.name) {
        countryName = option.data.name;
      } else return false;
    } else return false;
    const normalizedCountryName = removeAccents(countryName.toLowerCase());
    return normalizedCountryName.includes(searchValue);
  };

  // ============ EFFECTS - QuoterId & Details ============
  useEffect(() => {
    const id = getQuoterIdSafe();
    setQuoterId(id);
    setClientKey(getClientKeySafe());
  }, []);

  useEffect(() => {
    if (!quoterId) return;
    const fetchDetailsAndLog = async () => {
      try {
        const url = `${process.env.REACT_APP_API_BACKEND}/external/landing/details/${quoterId}`;
        const resp = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, credentials: 'include' });
        if (!resp.ok) return;
        const result = await resp.json();
        const q = (result?.data?.quoter) || result?.data || result?.quoter || null;
        if (!q) return;
        const mapped = {
          id: q.id ?? null, productId: q.productId ?? null, subproductId: q.subproductId ?? null,
          quoterValor: q.quoterValor ?? null, countryOriginName: q.countryOriginName ?? null,
          countryDestinationName: q.countryDestinationName ?? null, quoterNum: q.quoterNum ?? null,
          ages: q.ages ?? null, initialDate: (q.initialDate ?? q.initial_date) ?? null,
          endDate: (q.endDate ?? q.end_date) ?? null, productName: q.productName ?? null,
          productSubName: q.productSubName ?? null, countryOriginId: q.countryOriginId ?? null,
          countryDestinationId: q.country_destinations ?? null, email: q.email ?? null, phone: q.phone ?? null,
        };
        setQuoterDataFromDetails(mapped);
        setSelectedOrigin(q.countryOriginId);
        if (!quoterId && q.id) setQuoterId(String(q.id));
        if (q.email) setEmail(q.email);
        if (q.phone) setPhone(q.phone);
      } catch (e) {}
    };
    fetchDetailsAndLog();
  }, [quoterId]);

  // ============ EFFECTS - Insurance Reset ============
  useEffect(() => {
    if (selectedInsurance) {
      setSelectedOrigin(null);
      setSelectedDestinations([]);
      setProduct("");
      const initialCounts = {};
      if (selectedInsurance.passengers?.length > 0) {
        selectedInsurance.passengers.forEach(p => { initialCounts[p.id] = 0; });
      }
      setPassengerCounts(initialCounts);
    }
  }, [selectedInsurance]);

  // ============ EFFECTS - Preselection from Details ============
  useEffect(() => {
    const q = quoterDataFromDetails;
    if (!q) return;
    isHydrating.current = true;
    const parseDate = (d) => {
      if (!d) return null;
      if (d instanceof Date) return d;
      if (typeof d === 'string') {
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(d) ? `${d}T00:00:00` : d;
        const dt = new Date(iso);
        return isNaN(dt.getTime()) ? null : dt;
      }
      return null;
    };
    const s = parseDate(q.initialDate);
    const e = parseDate(q.endDate);
    if (s) setStartDate(s);
    const isRange = selectedInsurance && selectedInsurance.dateType === 'range';
    if (isRange) { setEndDate(e || null); } else { setEndDate(null); }
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const desiredId = quoterDataFromDetails?.countryOriginId;
    if (!desiredId || !selectedInsurance?.origins?.length) return;
    const match = selectedInsurance.origins.find(o => o?.country?.id && String(o.country.id) === String(desiredId));
    if (match?.country?.id != null) setSelectedOrigin(match.country.id);
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const desired = quoterDataFromDetails?.countryDestinationId;
    if (desired == null || !selectedInsurance?.destinations?.length) return;
    const arr = Array.isArray(desired) ? desired : [desired];
    const desiredSet = new Set(arr.map(id => String(id)));
    const validIds = selectedInsurance.destinations.map(d => d?.country?.id).filter(id => id != null && desiredSet.has(String(id)));
    setSelectedDestinations(validIds);
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const subId = quoterDataFromDetails?.subproductId;
    if (subId == null || !selectedInsurance?.subs?.length) return;
    const match = selectedInsurance.subs.find(s => String(s?.id) === String(subId));
    if (match?.id != null) setProduct(match.id);
  }, [quoterDataFromDetails, selectedInsurance]);

  useEffect(() => {
    const agesArr = quoterDataFromDetails?.ages;
    if (!Array.isArray(agesArr) || agesArr.length === 0 || !selectedInsurance?.passengers?.length) return;
    const nextCounts = {};
    agesArr.forEach(a => {
      if (!a) return;
      const target = selectedInsurance.passengers.find(p =>
        (p?.age_id != null && String(p.age_id) === String(a.id)) || String(p?.id) === String(a.id)
      );
      if (target && typeof a.count === 'number' && a.count >= 0) nextCounts[target.id] = a.count;
    });
    if (Object.keys(nextCounts).length > 0) setPassengerCounts(prev => ({ ...prev, ...nextCounts }));
  }, [quoterDataFromDetails, selectedInsurance]);

  // ============ EFFECTS - Geolocation ============
  useEffect(() => {
    if (!selectedInsurance?.origins?.length || selectedOrigin || geolocating || userClearedOrigin.current) return;
    const detectOriginCountry = async () => {
      setGeolocating(true);
      try {
        const response = await fetch('https://geo-detector.long-king-001d.workers.dev/');
        if (!response.ok) return;
        const data = await response.json();
        if (data.success && data.country) {
          const detectedCountry = data.country.toUpperCase();
          let matchingOrigin = selectedInsurance.origins.find(o => o?.country?.code?.toUpperCase() === detectedCountry);
          if (!matchingOrigin) {
            const countryNames = {
              'AR': 'Argentina', 'BO': 'Bolivia', 'BR': 'Brasil', 'CA': 'Canadá',
              'CL': 'Chile', 'CO': 'Colombia', 'CR': 'Costa Rica', 'EC': 'Ecuador',
              'SV': 'El Salvador', 'ES': 'España', 'GT': 'Guatemala', 'HN': 'Honduras',
              'MX': 'México', 'PA': 'Panamá', 'PY': 'Paraguay', 'PE': 'Perú',
              'DO': 'República Dominicana', 'UY': 'Uruguay', 'VE': 'Venezuela'
            };
            const countryName = countryNames[detectedCountry];
            if (countryName) matchingOrigin = selectedInsurance.origins.find(o => o?.country?.name === countryName);
          }
          if (!matchingOrigin) {
            matchingOrigin = selectedInsurance.origins.find(o => o?.country?.name === 'Mundial');
          }
          if (matchingOrigin?.country?.id) setSelectedOrigin(matchingOrigin.country.id);
        }
      } catch (error) {
      } finally {
        setGeolocating(false);
      }
    };
    const timer = setTimeout(detectOriginCountry, 300);
    return () => clearTimeout(timer);
  }, [selectedInsurance, selectedOrigin, geolocating]);

  // ============ EFFECTS - Timezone MinDate ============
  useEffect(() => {
    if (!selectedOrigin || !selectedInsurance?.origins) {
      setMinDateForPicker(new Date());
      return;
    }
    try {
      const originCountry = selectedInsurance.origins.find(o => o?.country?.id === selectedOrigin);
      if (!originCountry?.country?.name) { setMinDateForPicker(new Date()); return; }
      const timezone = COUNTRY_TIMEZONES[originCountry.country.name] || 'UTC';
      const nowInOriginTimezone = toZonedTime(new Date(), timezone);
      setMinDateForPicker(nowInOriginTimezone);
    } catch (error) {
      setMinDateForPicker(new Date());
    }
  }, [selectedOrigin, selectedInsurance]);

  // Auto-set destination = origin for Viaje Nacional
  useEffect(() => {
    const national = selectedInsurance?.name?.toLowerCase().includes('nacional');
    if (national && selectedOrigin) {
      setSelectedDestinations([selectedOrigin]);
    }
  }, [selectedOrigin, selectedInsurance]);

  // ============ API ============
  const fetchApiData = async () => {
    try {
      setLoading(true);
      setError(null);
      const productsResponse = await fetch(process.env.REACT_APP_API_BACKEND + '/external/quoter/products', {
        method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' }
      });
      if (!productsResponse.ok) throw new Error(`Error loading products: ${productsResponse.status}`);
      const productsData = await productsResponse.json();
      setApiData(productsData);

      // Fetch countries (not used directly but kept for compatibility)
      try {
        await fetch(process.env.REACT_APP_API_BACKEND + '/conf/countries', {
          method: 'GET', credentials: 'include', headers: { 'Content-Type': 'application/json' }
        });
      } catch (e) {}

      if (productsData?.data?.length > 0) {
        const firstActiveProduct = productsData.data.find(p => p.active);
        if (firstActiveProduct) {
          setSelectedInsurance(firstActiveProduct);
          if (firstActiveProduct.subs?.length > 0) setProduct(firstActiveProduct.subs[0].id);
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApiData(); }, []);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (destinationDebounceTimer.current) {
        clearTimeout(destinationDebounceTimer.current);
      }
      if (passengersDebounceTimer.current) {
        clearTimeout(passengersDebounceTimer.current);
      }
    };
  }, []);

  // Clear destination timer when user manually changes panel/step (desktop)
  useEffect(() => {
    if (activePanel !== 'destination' && destinationDebounceTimer.current) {
      clearTimeout(destinationDebounceTimer.current);
      destinationDebounceTimer.current = null;
    }
  }, [activePanel]);

  // Clear destination timer when user manually changes step (mobile)
  useEffect(() => {
    if (mobileActiveStep !== 'destination' && destinationDebounceTimer.current) {
      clearTimeout(destinationDebounceTimer.current);
      destinationDebounceTimer.current = null;
    }
  }, [mobileActiveStep]);

  // Clear passengers timer when user manually changes panel/step (desktop)
  useEffect(() => {
    if (activePanel !== 'passengers' && passengersDebounceTimer.current) {
      clearTimeout(passengersDebounceTimer.current);
      passengersDebounceTimer.current = null;
    }
  }, [activePanel]);

  // Clear passengers timer when user manually changes step (mobile)
  useEffect(() => {
    if (mobileActiveStep !== 'passengers' && passengersDebounceTimer.current) {
      clearTimeout(passengersDebounceTimer.current);
      passengersDebounceTimer.current = null;
    }
  }, [mobileActiveStep]);

  // Reset dates when origin country changes to match new timezone
  // Skip during hydration to preserve dates from database
  useEffect(() => {
    if (!selectedOrigin || !selectedInsurance) return;

    if (isHydrating.current) {
      isHydrating.current = false;
      return;
    }

    const originItem = selectedInsurance.origins?.find(o => o.country?.id === selectedOrigin);
    const originCountry = originItem?.country?.name || 'Mundial';
    const timezone = COUNTRY_TIMEZONES[originCountry] || 'UTC';
    const newMinDate = toZonedTime(new Date(), timezone);

    // Reset start date if it's before the new minimum date
    if (startDate < newMinDate) {
      setStartDate(newMinDate);
    }

    // Reset end date if it's before the new minimum date
    if (endDate < newMinDate) {
      setEndDate(newMinDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrigin, selectedInsurance]);

  // TODO: Auto-abrir contacto cuando cierra el panel de viajeros
  // (Temporalmente comentado - buscando solución estable)
  // useEffect(() => {
  //   if (prevActivePanel.current === 'passengers' && activePanel !== 'passengers' && activePanel !== 'contact') {
  //     if (showContact && layoutMode !== 'mobile') {
  //       if (passengersDebounceTimer.current) {
  //         clearTimeout(passengersDebounceTimer.current);
  //       }
  //       setTimeout(() => setActivePanel('contact'), 100);
  //     }
  //   }
  //   prevActivePanel.current = activePanel;
  // }, [activePanel, showContact, layoutMode]);

  // ============ VOICE ============
  const getProductConfigurations = async (productIds) => {
    try {
      const response = await fetch(process.env.REACT_APP_API_BACKEND + '/external/quoterlanding/product-config', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Origin': window.location.origin },
        body: JSON.stringify({ productIds })
      });
      if (!response.ok) throw new Error('Error');
      const result = await response.json();
      return result.success ? result.data : [];
    } catch (error) { return []; }
  };

  const handleSearchvoice = async (voiceText) => {
    setVoiceMessage('');
    setVoiceMessageType('');
    if (!voiceText?.trim()) {
      setVoiceMessage('Por favor, ingresa un texto antes de enviar.');
      setVoiceMessageType('error');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const voiceData = {
        voiceText: voiceText.trim(),
        client_id: apiData?.client?.client_id,
        client_name: apiData?.client?.client_name
      };
      if (selectedInsurance?.id) {
        voiceData.productIds = [selectedInsurance.id];
        try { await getProductConfigurations([selectedInsurance.id]); } catch (e) {}
      }
      const response = await fetch(process.env.REACT_APP_API_BACKEND + '/external/quoterlanding/validatevoice', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'Origin': window.location.origin },
        body: JSON.stringify(voiceData)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al procesar la solicitud de voz');
      }
      const result = await response.json();
      if (result.data.success) {
        const quoterData = {
          email: result.data.email || '', phone: result.data.phone || '',
          initial_date: result.data.start_date, end_date: result.data.end_date,
          countryOriginId: result.data.origin_country_id,
          country_destinations: result.data.destination_country_id,
          passengers: result.data.passengers_array,
          passengers_ages: result.data.passengers_array.length,
          productId: result.data.product_id,
          subproductId: result.data.subproduct_id || null,
          obs: result.data.resumen || '',
        };
        sendData(quoterData);
        setVoiceMessage(result.data.resumen);
        setVoiceMessageType('success');
      } else {
        setVoiceMessage(result.data.validacion);
        setVoiceMessageType('error');
      }
    } catch (err) {
      setError(err.message);
      setVoiceMessage(`Error: ${err.message}`);
      setVoiceMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleSendVoiceText = (text) => handleSearchvoice(text);

  // ============ GUIDANCE MODAL ============
  const scanCurrentQuoterFields = () => {
    let fieldStatus = [];
    let missingFields = [];
    let examples = [];
    if (!selectedInsurance) {
      return { hasProduct: false, fieldStatus: [], missingFields: ["tipo de seguro"], examples: ["Selecciona un tipo de seguro arriba"] };
    }
    const hasOriginSelector = selectedInsurance.origins?.length > 0;
    if (hasOriginSelector) {
      if (selectedOrigin) {
        const originItem = selectedInsurance.origins.find(o => o.country?.id === selectedOrigin);
        fieldStatus.push(`✅ Origen: ${originItem?.country?.name || 'Seleccionado'}`);
      } else {
        fieldStatus.push("❌ País de origen");
        missingFields.push("país de origen");
        examples.push("'Viajo desde Colombia'");
      }
    }
    const hasDestinationSelector = selectedInsurance.destinations?.length > 0;
    if (hasDestinationSelector) {
      if (selectedDestinations.length > 0) {
        fieldStatus.push(`✅ Destino: seleccionado`);
      } else {
        fieldStatus.push("❌ País(es) de destino");
        missingFields.push("destino");
        examples.push("'Voy a España y Francia'");
      }
    }
    if (startDate && endDate && startDate.getTime() !== endDate.getTime()) {
      fieldStatus.push(`✅ Fechas: ${startDate.toLocaleDateString('es', {day: '2-digit', month: 'short'})} - ${endDate.toLocaleDateString('es', {day: '2-digit', month: 'short'})}`);
    } else {
      fieldStatus.push("❌ Fechas de viaje");
      missingFields.push("fechas");
      examples.push("'Del 15 al 30 de marzo'");
    }
    if (guests > 0) {
      fieldStatus.push(`✅ Pasajeros: ${guests} total`);
    } else {
      fieldStatus.push("❌ Número de pasajeros");
      missingFields.push("pasajeros");
      examples.push("'Somos 2 adultos y 1 niño'");
    }
    return { hasProduct: true, productName: selectedInsurance.name, fieldStatus, missingFields, examples };
  };

  const generateGuidanceContent = () => {
    const scan = scanCurrentQuoterFields();
    if (!scan.hasProduct) return { title: "Selecciona un Seguro", message: "Primero elige el tipo de seguro.", requirements: [], examples: scan.examples };
    return {
      title: scan.missingFields.length > 0 ? `${scan.productName} - Faltan campos` : `${scan.productName} - ¡Listo!`,
      message: scan.missingFields.length > 0 ? `Campos faltantes: ${scan.missingFields.join(', ')}` : "¡Perfecto! Describe tu viaje.",
      requirements: scan.fieldStatus,
      examples: scan.examples.length > 0 ? scan.examples : ["'¿Cuánto cuesta?'"],
      tip: scan.missingFields.length > 0 ? "Ejemplo: " + scan.examples.join(' + ') : "Puedes preguntar precios o hacer cambios."
    };
  };

  const handleVoiceInputClick = () => {
    if (!hasShownInitialGuidance) {
      openGuidanceModal();
      setHasShownInitialGuidance(true);
    }
  };

  const openGuidanceModal = () => {
    setGuidanceContent(generateGuidanceContent());
    setShowGuidanceModal(true);
  };

  // ============ VALIDATION ============
  const validateFieldsWithErrors = () => {
    const errors = { destino: false, fechas: false, viajeros: false, producto: false, email: false };
    if (!selectedInsurance) return false;
    const hasSubproducts = selectedInsurance.subs?.length > 0;
    if (!hasSubproducts && selectedInsurance.destinations?.length > 0 && selectedDestinations.length === 0) {
      errors.destino = true;
    }
    if (selectedInsurance.dateType === 'range') {
      if (!startDate || !endDate) {
        errors.fechas = true;
      } else {
        const DAY_MS = 1000 * 60 * 60 * 24;
        const days = Math.floor((endDate - startDate) / DAY_MS) + 1;
        const minDays = isNational ? 2 : 3;
        if (days < minDays) errors.fechas = true;
      }
    } else if (selectedInsurance.dateType === 'single') {
      if (!startDate) errors.fechas = true;
    }
    if (guests === 0) errors.viajeros = true;
    if (hasSubproducts && !product) errors.producto = true;
    if (selectedInsurance.email && (!email || email.trim() === '')) errors.email = true;
    setValidationErrors(errors);
    return !Object.values(errors).some(e => e === true);
  };

  // ============ SUBMIT ============
  const sendData = async (quoterData) => {
    try {
      const utmParams = window?.QuoterConfig?.utmParams || null;
      const dataWithUTMs = { ...quoterData, utmParams };
      const idParam = (quoterData?.id != null) ? String(quoterData.id) : effectiveQuoterId;
      const url = `${process.env.REACT_APP_API_BACKEND}/external/quoterlanding/create?id=${encodeURIComponent(idParam)}`;
      const headers = { 'Content-Type': 'application/json' };
      if (clientKey) headers['x-client-key'] = clientKey;
      const response = await fetch(url, {
        method: 'POST', credentials: 'include',
        headers,
        body: JSON.stringify(dataWithUTMs)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al crear la cotización');
      }
      const result = await response.json();
      const uuidquoter = result.data.id;
      const locale = window?.QuoterConfig?.locale || 'es';
      const landing = process.env.REACT_APP_LANDING || 'https://segurosfly.com';
      const newTab = (() => {
        try {
          const scripts = Array.from(document.getElementsByTagName('script'));
          for (const s of scripts) {
            const src = s.getAttribute('src');
            if (src && src.includes('cotizador.js')) {
              return new URL(src, window.location.origin).searchParams.get('newTab') === 'true';
            }
          }
        } catch (e) {}
        return false;
      })();
      if (newTab) {
        window.open(`${landing}/${locale}/cotizar/plans/${uuidquoter}`, '_blank', 'noopener,noreferrer');
      } else {
        window.location.href = `${landing}/${locale}/cotizar/plans/${uuidquoter}`;
      }
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      alert('Error al crear la cotización: ' + error.message);
    }
  };

  // Helper function to format date without UTC conversion
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSearch = async () => {
    if (!validateFieldsWithErrors()) return false;

    try {
      const agesData = [];
      if (selectedInsurance.passengers?.length > 0) {
        selectedInsurance.passengers.forEach(passenger => {
          const count = passengerCounts[passenger.id] || 0;
          if (count > 0) {
            agesData.push({ id: passenger.age_id, name: passenger.name, count });
          }
        });
      }
      const quoterData = {
        email: email || '', phone: phone || '',
        marketing_consent: true,
        initial_date: formatLocalDate(startDate),
        end_date: formatLocalDate(endDate),
        countryOriginId: selectedOrigin?.id || selectedOrigin,
        country_destinations: selectedDestinations.map(dest => dest?.id || dest),
        passengers: agesData, passengers_ages: agesData.length,
        productId: selectedInsurance.id,
        subproductId: (product && typeof product === 'number' && product > 0) ? product : null,
        obs: '',
      };
      sendData(quoterData);
    } catch (error) {
      alert('Error al crear la cotización: ' + error.message);
    }
  };

  // ============ MEMOIZED OPTIONS ============
  const buildCountryOptions = (countries) => {
    if (!countries) return [];
    return countries.map(item => {
      if (!item.country) return null;
      const country = item.country;
      return {
        value: country.id,
        data: country,
        label: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {country.flag && (
              typeof country.flag === 'string' && (country.flag.startsWith('http') || country.flag.startsWith('/') || country.flag.endsWith('.svg') || country.flag.endsWith('.png') || country.flag.endsWith('.jpg') || country.flag.endsWith('.webp'))
                ? <img src={country.flag.startsWith('http') ? country.flag : `${process.env.REACT_APP_API_BACKEND}${country.flag.startsWith('/') ? '' : '/'}${country.flag}`} alt="" loading="lazy" decoding="async" style={{ width: '18px', height: '12px', objectFit: 'cover' }} />
                : <span style={{ fontSize: '16px' }}>{country.flag}</span>
            )}
            <span>{country.name}</span>
          </div>
        )
      };
    }).filter(Boolean);
  };

  const originOptions = useMemo(() => buildCountryOptions(selectedInsurance?.origins), [selectedInsurance]);
  const destinationOptions = useMemo(() => {
    const national = selectedInsurance?.name?.toLowerCase().includes('nacional');
    if (national) {
      // For national, destination options come from origins (same country)
      return buildCountryOptions(selectedInsurance?.origins);
    }
    const allOptions = buildCountryOptions(selectedInsurance?.destinations);
    // Filter out the selected origin to prevent selecting the same country
    return allOptions.filter(option => option.value !== selectedOrigin);
  }, [selectedInsurance, selectedOrigin]);

  // ============ SUMMARIES ============
  const getOriginSummary = () => {
    if (!selectedOrigin) return '';
    const opt = originOptions.find(o => o.value === selectedOrigin);
    return opt?.data?.name || '';
  };

  const getDestinationSummary = () => {
    if (selectedDestinations.length === 0) return '';
    const names = selectedDestinations.map(id => {
      const opt = destinationOptions.find(o => o.value === id);
      return opt?.data?.name;
    }).filter(Boolean);
    return names.join(', ');
  };

  const getProductSummary = () => {
    if (!product || !selectedInsurance?.subs) return '';
    const sub = selectedInsurance.subs.find(s => s.id === product);
    return sub?.name || '';
  };

  const getDateSummary = () => {
    if (!startDate) return '';
    const opts = { day: '2-digit', month: 'short', year: '2-digit' };
    if (selectedInsurance?.dateType === 'range' && endDate) {
      return `${startDate.toLocaleDateString('es', opts)} - ${endDate.toLocaleDateString('es', opts)}`;
    }
    if (selectedInsurance?.dateType === 'date') {
      // Calculate end date: startDate + 364 days = 365 days inclusive
      const calculatedEndDate = new Date(startDate);
      calculatedEndDate.setDate(calculatedEndDate.getDate() + 364);
      return `${startDate.toLocaleDateString('es', opts)} - ${calculatedEndDate.toLocaleDateString('es', opts)}`;
    }
    return startDate.toLocaleDateString('es', opts);
  };

  const getPassengersSummary = () => {
    if (guests === 0) return '';
    return `${guests} ${guests === 1 ? 'viajero' : 'viajeros'}`;
  };

  // ============ CONDITIONAL VISIBILITY ============
  const isNational = selectedInsurance?.name?.toLowerCase().includes('nacional');
  selectedInsuranceRef.current = selectedInsurance;

  const hasActiveProducts = apiData?.data?.length > 0 && apiData.data.some(p => p.active);
  const showOrigin = selectedInsurance?.origins?.length > 0;
  const showDestination = selectedInsurance?.destinations?.length > 0 && (!selectedInsurance?.subs || selectedInsurance.subs.length === 0);
  const showProduct = selectedInsurance?.subs?.length > 0;
  const showDates = selectedInsurance && selectedInsurance.dateType && selectedInsurance.dateType !== 'NA';
  const showContact = selectedInsurance && (selectedInsurance.email || selectedInsurance.phone);
  const showEmail = selectedInsurance && selectedInsurance.email;
  const showPhone = selectedInsurance && selectedInsurance.phone;
  const showVoice = apiData?.client?.activeai === 1;

  // ============ HANDLER CALLBACKS ============
  const handlePassengerCountChange = useCallback((passengerId, newCount) => {
    setPassengerCounts(prev => {
      const updated = { ...prev, [passengerId]: newCount };

      // Check if we have at least one passenger
      const totalPassengers = Object.values(updated).reduce((sum, count) => sum + count, 0);

      if (totalPassengers > 0) {
        setValidationErrors(prev => ({ ...prev, viajeros: false }));
      }

      return updated;
    });

    // Clear any existing timer
    if (passengersDebounceTimer.current) {
      clearTimeout(passengersDebounceTimer.current);
    }

    // Auto-advance desactivado
  }, []);

  const handleDateRangeChange = useCallback((dates) => {
    const [start, end] = dates;
    setStartDate(start);
    if (start && end) {
      const DAY_MS = 1000 * 60 * 60 * 24;
      const days = Math.floor((end - start) / DAY_MS) + 1;
      const national = selectedInsuranceRef.current?.name?.toLowerCase().includes('nacional');
      const minDays = national ? 2 : 3;
      if (days < minDays) {
        const adjustedEnd = new Date(start);
        adjustedEnd.setDate(adjustedEnd.getDate() + minDays - 1);
        setEndDate(adjustedEnd);
        setMinDaysAdjusted(true);
      } else {
        setEndDate(end);
        setMinDaysAdjusted(false);
      }
      setValidationErrors(prev => ({ ...prev, fechas: false }));
    } else {
      setEndDate(end);
      setMinDaysAdjusted(false);
    }
  }, []);

  const handleSingleDateChange = useCallback((date) => {
    setStartDate(date);
    if (date && selectedInsurance?.dateType === 'date') {
      const end = new Date(date);
      end.setDate(end.getDate() + 364);
      setEndDate(end);
    }
    if (date) {
      setValidationErrors(prev => ({ ...prev, fechas: false }));

      // Auto-advance desactivado
    }
  }, [selectedInsurance?.dateType]);

  const handleDestinationInputChange = useCallback(() => {
    // Cuando el usuario está escribiendo, cancelar el timer de auto-avance
    if (destinationDebounceTimer.current) {
      clearTimeout(destinationDebounceTimer.current);
      destinationDebounceTimer.current = null;
    }
  }, []);

  const handleDestinationChange = useCallback((dests) => {
    setSelectedDestinations(dests);
    if (dests.length > 0) {
      setValidationErrors(prev => ({ ...prev, destino: false }));

      // Cancelar timer anterior si existe
      if (destinationDebounceTimer.current) {
        clearTimeout(destinationDebounceTimer.current);
      }

      // Auto-advance desactivado
    }
  }, []);

  const handleProductChange = useCallback((val) => {
    setProduct(val);
    if (val) {
      setValidationErrors(prev => ({ ...prev, producto: false }));

      // Auto-advance desactivado
    }
  }, []);

  const handleEmailChange = useCallback((val) => {
    setEmail(val);
    if (val.trim()) setValidationErrors(prev => ({ ...prev, email: false }));
  }, []);

  const handlePhoneChange = useCallback((val) => {
    if (val && !val.startsWith('+')) {
      setPhone('+' + val);
    } else {
      setPhone(val);
    }
  }, []);

  // ============ RENDER FIELD FUNCTIONS (shared between desktop & mobile) ============
  const renderInsuranceContent = () => (
    <InsuranceTypeField
      products={apiData?.data || []}
      selectedInsurance={selectedInsurance}
      onSelect={(ins) => {
        if (layoutMode === 'mobile') {
          // flushSync: fuerza render sincrónico para que el input del siguiente paso
          // exista en el DOM antes de llamar focus(), dentro del contexto del gesto
          const nextStep = showOrigin ? 'origin' : showDestination ? 'destination' : 'dates';
          const isKeyboard = nextStep === 'origin' || nextStep === 'destination';
          flushSync(() => {
            setSelectedInsurance(ins);
            setMobileActiveStep(nextStep);
          });
          if (isKeyboard) {
            const input = document.querySelector('.sf-mobile-sheet__fullscreen-content .react-select__input input');
            if (input) input.focus();
          }
        } else {
          setSelectedInsurance(ins);
          setTimeout(() => {
            if (showOrigin) setActivePanel('origin');
            else if (showDestination) setActivePanel('destination');
            else if (showProduct) setActivePanel('product');
            else setActivePanel('dates');
          }, 200);
        }
      }}
    />
  );

  const renderOriginContent = () => (
    <OriginField
      options={originOptions}
      value={selectedOrigin}
      onChange={(val) => {
        if (!val) {
          userClearedOrigin.current = true;
        } else {
          userClearedOrigin.current = false;
        }
        setSelectedOrigin(val);
        // Remove the new origin from destinations if it's already selected there
        if (val && selectedDestinations.includes(val)) {
          setSelectedDestinations(prev => prev.filter(destId => destId !== val));
        }
            // Auto-advance desactivado
      }}
      customFilterOption={customFilterOption}
      onMenuOpen={() => { setIsDatePickerOpen(false); }}
      isOpen={layoutMode === 'mobile' ? mobileActiveStep === 'origin' : activePanel === 'origin'}
    />
  );

  const renderDestinationContent = () => (
    <DestinationField
      options={destinationOptions}
      value={selectedDestinations}
      onChange={handleDestinationChange}
      onInputChange={handleDestinationInputChange}
      customFilterOption={customFilterOption}
      validationError={validationErrors.destino}
      onMenuOpen={() => { setIsDatePickerOpen(false); }}
      isOpen={layoutMode === 'mobile' ? mobileActiveStep === 'destination' : activePanel === 'destination'}
      isDisabled={isNational}
    />
  );

  const renderProductContent = () => (
    <ProductField
      subs={selectedInsurance?.subs || []}
      value={product}
      onChange={handleProductChange}
      validationError={validationErrors.producto}
    />
  );

  const renderDatesContent = () => (
    <>
      {minDaysAdjusted && (
        <div className="sf-min-days-msg">
          {isNational
            ? 'Las coberturas aplican desde 2 días de viaje'
            : 'Las coberturas aplican desde 3 días de viaje'}
        </div>
      )}
      <DateField
      key={`datepicker-${selectedOrigin}`}
      dateType={selectedInsurance?.dateType}
      startDate={startDate}
      endDate={endDate}
      onChangeRange={handleDateRangeChange}
      onChangeSingle={handleSingleDateChange}
      minDate={minDateForPicker}
      maxDays={isNational ? 90 : 365}
      isOpen={isDatePickerOpen}
      onOpen={() => { setIsDatePickerOpen(true); }}
      onClose={() => setIsDatePickerOpen(false)}
      validationError={validationErrors.fechas}
      inline={layoutMode === 'mobile' || activePanel === 'dates'}
      monthsShown={layoutMode === 'mobile' ? mobileMonthsShown : 2}
      onLoadMoreMonths={() => setMobileMonthsShown(prev => prev + 6)}
      isMobile={layoutMode === 'mobile'}
    />
    </>
  );

  const renderPassengersContent = () => (
    <PassengersField
      passengers={selectedInsurance?.passengers || []}
      counts={passengerCounts}
      onCountChange={handlePassengerCountChange}
      validationError={validationErrors.viajeros}
    />
  );

  // renderContactContent removed - email field is always visible in desktop searchbar

  // ============ RENDER ============
  return (
    <div className="sf-quoter" style={{ width: '100%' }}>
      {loading && (
        <div className="sf-loading">Cargando datos...</div>
      )}

      {error && (
        <div className="sf-error">Error: {error}</div>
      )}

      {!loading && !hasActiveProducts && (
        <div className="sf-no-products">
          No hay productos disponibles. Contáctanos para asistencia.
        </div>
      )}

      {!loading && hasActiveProducts && (
        <>
          {/* DESKTOP / TABLET */}
          {(layoutMode === 'desktop' || layoutMode === 'tablet') && (
            <SearchBar
              selectedInsurance={selectedInsurance}
              products={apiData?.data || []}
              showOrigin={showOrigin}
              originSummary={getOriginSummary()}
              showDestination={showDestination}
              destinationSummary={getDestinationSummary()}
              showProduct={showProduct}
              productSummary={getProductSummary()}
              showDates={showDates}
              dateSummary={getDateSummary()}
              passengersSummary={getPassengersSummary()}
              showContact={showContact}
              showEmail={showEmail}
              showPhone={showPhone}
              email={email}
              onEmailChange={handleEmailChange}
              emailError={validationErrors.email}
              phone={phone}
              onPhoneChange={handlePhoneChange}
              phoneError={false}
              destinoError={validationErrors.destino}
              viajerosError={validationErrors.viajeros}
              fechasError={validationErrors.fechas}
              productoError={validationErrors.producto}
              showVoice={showVoice}
              onVoiceClick={() => {/* scroll to voice input */}}
              activePanel={activePanel}
              onPanelChange={setActivePanel}
              onSearch={handleSearch}
              renderInsurancePanel={renderInsuranceContent}
              renderOriginPanel={renderOriginContent}
              renderDestinationPanel={renderDestinationContent}
              renderProductPanel={renderProductContent}
              renderDatesPanel={renderDatesContent}
              renderPassengersPanel={renderPassengersContent}
            />
          )}

          {/* MOBILE */}
          {layoutMode === 'mobile' && (
            <>
              {/* Trigger bar */}
              <div className="sf-mobile-trigger" onClick={() => setIsMobileFlowOpen(true)}>
                <div className="sf-mobile-trigger__content">
                  <span className="sf-mobile-trigger__title">Cotiza tu Seguro</span>
                </div>
                <div className="sf-mobile-trigger__actions">
                  {showVoice && (
                    <button type="button" className="sf-mobile-trigger__voice" onClick={(e) => { e.stopPropagation(); }}>
                      <FiMic />
                    </button>
                  )}
                  <button type="button" className="sf-mobile-trigger__search">
                    <FaCalculator />
                  </button>
                </div>
              </div>

              <MobileOverlay
                isOpen={isMobileFlowOpen}
                onClose={() => setIsMobileFlowOpen(false)}
                activeStep={mobileActiveStep}
                onStepChange={setMobileActiveStep}
                onSearch={async () => { const ok = await handleSearch(); if (ok !== false) setIsMobileFlowOpen(false); }}
                selectedInsurance={selectedInsurance}
                insuranceSummary={selectedInsurance?.name?.replace('Multiples', 'Múltiples') || ''}
                renderInsurance={renderInsuranceContent}
                showOrigin={showOrigin}
                originSummary={getOriginSummary()}
                renderOrigin={renderOriginContent}
                showDestination={showDestination}
                destinationSummary={getDestinationSummary()}
                renderDestination={renderDestinationContent}
                showProduct={showProduct}
                productSummary={getProductSummary()}
                renderProduct={renderProductContent}
                showDates={showDates}
                dateSummary={getDateSummary()}
                renderDates={renderDatesContent}
                passengersSummary={getPassengersSummary()}
                renderPassengers={renderPassengersContent}
                showEmail={showEmail}
                showPhone={showPhone}
                email={email}
                onEmailChange={handleEmailChange}
                emailError={validationErrors.email}
                phone={phone}
                onPhoneChange={handlePhoneChange}
                phoneError={false}
              />
            </>
          )}

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="sf-success-message">¡Cotización enviada con éxito!</div>
          )}
        </>
      )}

      {/* Voice Input */}
      {showVoice && (
        <div style={{ marginTop: '8px', width: '100%', maxWidth: '1200px', margin: '8px auto 0 auto' }}>
          <VoiceInput
            onSendText={handleSendVoiceText}
            onInputClick={handleVoiceInputClick}
            onHelpClick={openGuidanceModal}
            placeholder="Describe tu viaje: 'Voy a Colombia por 15 días...' o haz preguntas"
          />
        </div>
      )}

      {/* Voice Response */}
      {voiceMessage && (
        <div className={`sf-voice-message sf-voice-message--${voiceMessageType}`}>
          {voiceMessage}
        </div>
      )}

      {/* Guidance Modal */}
      {showGuidanceModal && (
        <div className="sf-guidance-overlay" onClick={() => setShowGuidanceModal(false)}>
          <div className="sf-guidance-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="sf-guidance-title">{guidanceContent.title}</h2>
            <p className="sf-guidance-message">{guidanceContent.message}</p>

            {guidanceContent.requirements?.length > 0 && (
              <div className="sf-guidance-section">
                <h3 className="sf-guidance-section-title">Estado de tu Cotización:</h3>
                <div className="sf-guidance-list">
                  {guidanceContent.requirements.map((req, i) => (
                    <div key={i} className="sf-guidance-item">{req}</div>
                  ))}
                </div>
              </div>
            )}

            {guidanceContent.examples?.length > 0 && (
              <div className="sf-guidance-section sf-guidance-section--blue">
                <div className="sf-guidance-section-title">Ejemplos:</div>
                {guidanceContent.examples.map((ex, i) => (
                  <div key={i} className="sf-guidance-example">{ex}</div>
                ))}
              </div>
            )}

            {guidanceContent.tip && (
              <div className="sf-guidance-tip">{guidanceContent.tip}</div>
            )}

            <div style={{ textAlign: 'center', marginTop: '16px' }}>
              <button className="sf-guidance-close-btn" onClick={() => setShowGuidanceModal(false)}>
                ¡Entendido! Comenzar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Quoter;
